/**
 * Service layer — Inspiration board (database/298, api/inspiration.js).
 *
 * One board per project, two ways in:
 *   - CLIENT, from the Client Hub: scope = { token }   (Hub token + cached PIN proof)
 *   - TEAM, from the outline:      scope = { projectId } (Clerk session)
 * A team member previewing the Hub while signed in passes { token } too; the server recognizes
 * the Clerk session and treats them as team on the same board.
 *
 * Every write returns the whole refreshed board, so callers just replace their state.
 */

import { getCachedPinProof, getOptionalStaffToken, isPreviewToken } from './journey.js'

/*
 * PREVIEW (docs/CLIENT_HUB_PREVIEW.md). With a preview pass in scope.token the board is READ from
 * the server exactly as that client sees it, then every change is applied to a copy held in this
 * page only — no row, upload or Slack ping. The server refuses a pass on every write action anyway.
 * The rules below mirror api/inspiration.js's client rules (a client may only rewrite or delete
 * their own side's pins, boards and comments).
 */
const previewBoards = new Map()   // pass -> the board as it stands in this preview

function previewSort(items) {
  return [...items].sort((a, b) => {
    const pa = a.position, pb = b.position
    if (pa == null && pb != null) return -1
    if (pb == null && pa != null) return 1
    if (pa != null && pb != null && pa !== pb) return pa - pb
    return String(b.createdAt || '').localeCompare(String(a.createdAt || ''))
  })
}
function previewTop(board) {
  const ps = board.items.map(i => i.position).filter(p => p != null)
  return ps.length ? Math.min(...ps) : 0
}
function previewId() { return 'preview-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7) }
function forbidden() { const e = new Error('forbidden'); e.code = 'forbidden'; return e }

function previewNewItem(board, fields) {
  return {
    id: previewId(), collectionId: null, kind: 'upload', imageUrl: null, position: null, width: null, height: null,
    sourceUrl: null, title: null, note: null, favorite: false, addedByKind: 'client',
    addedByName: (board.me && board.me.name) || 'Client', createdAt: new Date().toISOString(), comments: [], ...fields,
  }
}

function previewWrite(token, action, a) {
  const cur = previewBoards.get(token)
  if (!cur) throw new Error('The board has not loaded yet.')
  const board = { ...cur, collections: [...cur.collections], items: cur.items.map(i => ({ ...i, comments: [...i.comments] })) }
  const item = id => { const it = board.items.find(i => i.id === id); if (!it) { const e = new Error('not_found'); e.code = 'not_found'; throw e } return it }
  let extra = {}
  if (action === 'updateItem') {
    const it = item(a.itemId)
    if ('collectionId' in a) it.collectionId = a.collectionId || null
    if ('favorite' in a) it.favorite = !!a.favorite
    if ('note' in a) { if (it.addedByKind !== 'client') throw forbidden(); it.note = String(a.note || '').trim() || null }
  } else if (action === 'deleteItem') {
    if (item(a.itemId).addedByKind !== 'client') throw forbidden()
    board.items = board.items.filter(i => i.id !== a.itemId)
  } else if (action === 'moveItem') {
    const it = item(a.itemId)
    const prev = a.prevId ? item(a.prevId) : null, next = a.nextId ? item(a.nextId) : null
    const p = prev ? prev.position : null, n = next ? next.position : null
    it.position = (p != null && n != null) ? (p + n) / 2 : p != null ? p + 1 : n != null ? n - 1 : 0
  } else if (action === 'addComment') {
    const text = String(a.body || '').trim()
    if (!text) throw new Error('body required')
    item(a.itemId).comments.push({ id: previewId(), body: text, internal: false, authorKind: 'client',
      authorName: (board.me && board.me.name) || 'Client', createdAt: new Date().toISOString(), mine: true })
  } else if (action === 'deleteComment') {
    for (const it of board.items) {
      const c = it.comments.find(x => x.id === a.commentId)
      if (!c) continue
      if (!c.mine) throw forbidden()
      it.comments = it.comments.filter(x => x.id !== a.commentId)
    }
  } else if (action === 'createCollection') {
    const name = String(a.name || '').trim()
    if (!name) throw new Error('name required')
    const id = previewId()
    board.collections.push({ id, name, createdByKind: 'client' })
    extra = { createdId: id }
  } else if (action === 'renameCollection' || action === 'deleteCollection') {
    const col = board.collections.find(c => c.id === a.collectionId)
    if (!col) { const e = new Error('not_found'); e.code = 'not_found'; throw e }
    if (col.createdByKind !== 'client') throw forbidden()
    if (action === 'renameCollection') col.name = String(a.name || '').trim() || col.name
    else {
      board.collections = board.collections.filter(c => c.id !== col.id)
      board.items.forEach(i => { if (i.collectionId === col.id) i.collectionId = null })
    }
  } else if (action === 'addLink') {
    const url = String(a.url || '').trim()
    if (!url) throw new Error('url required')
    board.items.push(previewNewItem(board, { kind: 'link', imageUrl: url, sourceUrl: a.sourceUrl || url,
      note: a.note || null, collectionId: a.collectionId || null, position: previewTop(board) - 1 }))
  } else if (action === 'addItems') {
    const top = previewTop(board), list = a.items || []
    list.forEach((it, i) => board.items.push(previewNewItem(board, { imageUrl: it.imageUrl, width: it.width || null,
      height: it.height || null, note: it.note || null, collectionId: a.collectionId || null, position: top - list.length + i })))
  } else {
    throw new Error('unknown action')
  }
  board.items = previewSort(board.items)
  previewBoards.set(token, board)
  return { ...board, ...extra }
}

async function call(scope, action, args = {}) {
  const preview = !!(scope.token && isPreviewToken(scope.token))
  if (preview && action !== 'board') return previewWrite(scope.token, action, args)
  if (preview && previewBoards.has(scope.token)) return previewBoards.get(scope.token)
  const body = { action, ...args }
  if (scope.token) { body.t = scope.token; const p = getCachedPinProof(scope.token); if (p) body.pinProof = p }
  else body.projectId = scope.projectId
  const headers = { 'Content-Type': 'application/json' }
  const staff = await getOptionalStaffToken(scope.token)
  if (staff) headers.Authorization = 'Bearer ' + staff
  const r = await fetch('/api/inspiration', { method: 'POST', headers, body: JSON.stringify(body) })
  const j = await r.json().catch(() => ({}))
  if (!r.ok) {
    const e = new Error(j.error || ('Request failed (' + r.status + ')'))
    e.code = j.error
    throw e
  }
  if (preview) previewBoards.set(scope.token, j)
  return j
}

export const getBoard = (scope) => call(scope, 'board')
/** Counts only (team outline node): { total, unsorted, favorites, fromClient, latest, collections:[{id,name,count}] } */
export const getBoardSummary = (projectId) => call({ projectId }, 'summary')
export const updateItem = (scope, itemId, patch) => call(scope, 'updateItem', { itemId, ...patch })
export const deleteItem = (scope, itemId) => call(scope, 'deleteItem', { itemId })
/** Rearrange: put a picture between `prevId` and `nextId` (either may be null at an end). Shared by everyone. */
export const moveItem = (scope, itemId, prevId, nextId) => call(scope, 'moveItem', { itemId, prevId: prevId || null, nextId: nextId || null })
export const addComment = (scope, itemId, body, internal = false) => call(scope, 'addComment', { itemId, body, internal })
export const deleteComment = (scope, commentId) => call(scope, 'deleteComment', { commentId })
export const createCollection = (scope, name) => call(scope, 'createCollection', { name })
export const renameCollection = (scope, collectionId, name) => call(scope, 'renameCollection', { collectionId, name })
export const deleteCollection = (scope, collectionId) => call(scope, 'deleteCollection', { collectionId })
/** Save a picture from the web. `url` is the page or the picture; `sourceUrl` is the page a dragged picture came from. */
export const addLink = (scope, url, { note, collectionId, sourceUrl } = {}) => call(scope, 'addLink', { url, note, collectionId, sourceUrl })

const MAX_DIM = 2400

// Types the server stores as-is (api/inspiration.js IMAGE_EXT). Anything else is re-saved as JPEG.
const STORED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif', 'image/avif']
const EXT_TYPE = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp',
  heic: 'image/heic', heif: 'image/heif', avif: 'image/avif', bmp: 'image/bmp', tif: 'image/tiff', tiff: 'image/tiff' }

/** What kind of picture a file really is. iPhone photos picked from Files or Dropbox often come with
 *  no type (or 'image/jpg'), so read the first bytes, then the file name, before giving up. */
async function realImageType(file) {
  const t = String(file.type || '').toLowerCase()
  if (t === 'image/jpg' || t === 'image/pjpeg') return 'image/jpeg'
  if (/^image\/hei[cf]-sequence$/.test(t)) return 'image/heic'
  if (/^image\//.test(t)) return t
  try {
    const b = new Uint8Array(await file.slice(0, 16).arrayBuffer())
    const s = (a, z) => String.fromCharCode(...b.slice(a, z))
    if (b[0] === 0xff && b[1] === 0xd8) return 'image/jpeg'
    if (b[0] === 0x89 && s(1, 4) === 'PNG') return 'image/png'
    if (s(0, 3) === 'GIF') return 'image/gif'
    if (s(0, 4) === 'RIFF' && s(8, 12) === 'WEBP') return 'image/webp'
    if (s(4, 8) === 'ftyp') {
      const brand = s(8, 12)
      if (/avif|avis/.test(brand)) return 'image/avif'
      if (/heic|heix|hevc|mif1|msf1/.test(brand)) return 'image/heic'
    }
  } catch { /* fall through to the name */ }
  const ext = (String(file.name || '').match(/\.([a-z0-9]+)$/i) || [])[1]
  return (ext && EXT_TYPE[ext.toLowerCase()]) || null
}

/** The picture files among `files`, each carrying its real type. Everything else is left out. */
export async function pictureFiles(files) {
  const out = []
  for (const f of Array.from(files || [])) {
    const type = await realImageType(f)
    if (!type) continue
    out.push(type === f.type ? f : new File([f], f.name || 'picture', { type, lastModified: f.lastModified }))
  }
  return out
}

/** Shrink a large photo to at most MAX_DIM on its long side (JPEG). GIFs keep their animation and
 *  anything the browser can't decode (e.g. HEIC in Chrome) goes up as-is. */
async function prepareImage(file) {
  // GIFs keep their animation. A background tab gets its canvas work throttled to a crawl (measured:
  // ~40s per picture), so if the client has switched tabs, send the original rather than stall.
  const heic = /hei[cf]/i.test(file.type)
  if (file.type === 'image/gif' || (!heic && typeof document !== 'undefined' && document.hidden)) return { blob: file, type: file.type }
  try {
    const bmp = await createImageBitmap(file)
    const { width, height } = bmp
    const scale = Math.min(1, MAX_DIM / Math.max(width, height))
    // HEIC is always re-saved as JPEG (only Safari can show HEIC), even when it is already small.
    if (scale === 1 && file.size < 3 * 1024 * 1024 && !heic && STORED_TYPES.includes(file.type)) { bmp.close?.(); return { blob: file, type: file.type, width, height } }
    const w = Math.round(width * scale), h = Math.round(height * scale)
    const c = document.createElement('canvas'); c.width = w; c.height = h
    const g = c.getContext('2d'); g.fillStyle = '#fff'; g.fillRect(0, 0, w, h); g.drawImage(bmp, 0, 0, w, h)
    bmp.close?.()
    const blob = await new Promise(res => c.toBlob(res, 'image/jpeg', 0.86))
    return blob ? { blob, type: 'image/jpeg', width: w, height: h } : { blob: file, type: file.type, width, height }
  } catch { return { blob: file, type: file.type } }
}

/**
 * Upload image files and pin them. onProgress(done, total) is called as each file lands.
 * Returns the refreshed board, plus `skipped`: photos this browser cannot decode (Apple HEIC outside
 * Safari) are left out rather than uploaded as a picture nobody can see.
 */
export async function uploadImages(scope, files, { collectionId, note, onProgress } = {}) {
  const list = await pictureFiles(files)
  if (!list.length) throw new Error('Choose one or more image files.')
  // One at a time: decoding many large phone photos at once can run a phone out of memory.
  const prepared = []
  let skipped = 0
  for (const f of list) {
    const p = await prepareImage(f)
    if (/hei[cf]/i.test(p.type) && !p.width) { skipped++; continue }
    // A type the server doesn't store (BMP, TIFF) that this browser also couldn't re-save: skip it too.
    if (!STORED_TYPES.includes(p.type)) { skipped++; continue }
    prepared.push(p)
  }
  if (!prepared.length) return { ...(await call(scope, 'board')), skipped }
  // Preview: the pictures show from this browser's memory and are never uploaded.
  if (scope.token && isPreviewToken(scope.token)) {
    onProgress && onProgress(prepared.length, prepared.length)
    const board = previewWrite(scope.token, 'addItems', {
      collectionId: collectionId || null,
      items: prepared.map(p => ({ imageUrl: URL.createObjectURL(p.blob), width: p.width, height: p.height, note: note || '' })),
    })
    return { ...board, skipped }
  }
  const { uploads } = await call(scope, 'uploadUrls', { files: prepared.map(p => ({ type: p.type })) })
  let done = 0
  onProgress && onProgress(0, prepared.length)
  const queue = prepared.map((p, i) => i)
  async function worker() {
    while (queue.length) {
      const i = queue.shift()
      const p = prepared[i]
      const r = await fetch(uploads[i].uploadUrl, { method: 'PUT', headers: { 'Content-Type': p.type }, body: p.blob })
      if (!r.ok) throw new Error(r.status === 413 || r.status === 400
        ? 'One of those pictures is too large to add (the limit is 25 MB).'
        : 'A picture didn\u2019t upload (' + r.status + '). Please try again.')
      done++; onProgress && onProgress(done, prepared.length)
    }
  }
  await Promise.all([worker(), worker(), worker()])
  const board = await call(scope, 'addItems', {
    collectionId: collectionId || null,
    items: prepared.map((p, i) => ({ path: uploads[i].path, width: p.width, height: p.height, note: note || '' })),
  })
  return { ...board, skipped }
}
